import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CalendarEvent,
  MeetingStepperDetails,
  Place,
} from '../interfaces/interfaces';
import { AuthService } from './authService/auth.service';
import { MyBookingStore } from '../shared/my-bookings-item/my-bookings.store';
import { BookingsStore } from '../stores/bookings.store';
import { PlacesStore } from '../stores/places.store';

interface EventNotification {
  action: EventType;
  event: CalendarEvent;
}

enum EventType {
  Delete,
  Update,
  Create,
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private readonly bookingsStore = inject(BookingsStore);
  private readonly placesStore = inject(PlacesStore);

  private connection: signalR.HubConnection;

  public errorMessage$ = new Subject<any>();
  public bookingStepperResult$ = new Subject<any>();

  constructor(private authService: AuthService) {}

  public async createConnection() {
    this.connection = await new signalR.HubConnectionBuilder()
      .withUrl(environment.baseUrl + '/ConnectionHub', {
        accessTokenFactory: () => this.authService.fetchValidToken(),
      })
      .withAutomaticReconnect()
      .build();
    this.connection.start().then(() => {
      console.log('connection started');

      this.eventsListener();
      this.availabilityListener();
      this.errorListener();
    });
  }

  private errorListener() {
    this.connection.on('ErrorCallBack', (error) => {
      this.connection
        .stop()
        .then((err) =>
          console.log('there was an error in the server side:', error)
        );
      this.errorMessage$.next(error);
    });
  }

  private eventsListener() {
    this.connection.on('EventCallBack', (data: EventNotification) => {
      switch (data.action) {
        case EventType.Create:
          this.bookingsStore.addBookingLocal(data.event);
          break;
        case EventType.Update:
          this.bookingsStore.updateBookingLocal(data.event);
          break;
        case EventType.Delete:
          this.bookingsStore.deleteBookingLocal(data.event);
          break;
      }
    });
  }

  private availabilityListener() {
    this.connection.on('AvailabilityCallBack', (place: Place) => {
      this.placesStore.updatePlaceAvailabilityLocal(place);
    });
  }

  public async createConnectionAndInvokeSearchPlacesForBookingStepper(
    meetingDetails: MeetingStepperDetails
  ) {
    const meetingDetailsJSON = JSON.stringify(meetingDetails);
    const token = await this.authService.fetchValidToken();
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.baseUrl + '/SparoConnectionHub', {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('SearchAvailablePlacesWithCapacity', (data) => {
      this.bookingStepperResult$.next(data);
    });
    this.connection.start().then(() => {
      console.log('connection started');
      //calling the API function
      this.connection
        .invoke('SearchAvailablePlacesWithCapacityPriority', meetingDetailsJSON)
        .catch((err) => console.log('invokation canceled'));
      this.errorListener();
    });
  }

  public stopConnection() {
    if (this.connection?.state == 'Connected') {
      this.connection.stop();
    }
  }
}
